/**
 * Real past-paper questions for the landing page's try-a-question demo.
 * Hardcoded (not fetched) so the section renders instantly with no auth and
 * no backend round-trip. Every entry was verified by hand against its paper's
 * worked solutions; solutions are lightly rewritten to be KaTeX-safe (the
 * originals use TikZ diagrams / bare alignment characters).
 */
export type DemoQuestion = {
  subject: string;
  paperMeta: string;
  year: string;
  questionNumber: number;
  text: string;
  options: Record<string, string>;
  answer: string;
  workedSolution: string;
};

export const DEMO_QUESTIONS: Record<string, DemoQuestion[]> = {
  TMUA: [
    {
      subject: "Mathematics",
      paperMeta: "Paper 1",
      year: "2023",
      questionNumber: 2,
      text: String.raw`The graphs of $y = x^2 + 5x + 6$ and $y=mx-3$, where $m$ is a constant, are plotted on the same set of axes.
Given that the graphs do not meet, what is the complete range of possible values of $m$?`,
      options: {
        A: String.raw`$-1<m< 11$`,
        B: String.raw`$m<-1, m > 11$`,
        C: String.raw`$-\sqrt{11} < m < \sqrt{11}$`,
        D: String.raw`$m<-\sqrt{11}, m> \sqrt{11}$`,
        E: String.raw`$-11<m<1$`,
        F: String.raw`$m<-11, m> 1$`,
      },
      answer: "A",
      workedSolution: String.raw`As the graphs do not meet, $x^2 + 5x + 6 = mx - 3$ has no solutions, so the quadratic
$$ x^2 + (5 - m)x + 9 = 0 $$
must have a negative discriminant:
$$ (5 - m)^2 - 36 = m^2 - 10m - 11 < 0 $$
Factorising gives
$$ (m - 11)(m + 1) < 0 $$
which is negative between the roots, so $-1 < m < 11$, which is option A.`,
    },
    {
      subject: "Mathematics",
      paperMeta: "Paper 1",
      year: "2023",
      questionNumber: 13,
      text: String.raw`Point $P$ lies on the circle with equation $(x - 2)^2 + (y - 1)^2 = 16$
Point $Q$ lies on the circle with equation $(x - 4)^2 + (y + 5)^2 = 16$
What is the maximum possible length of $PQ$?`,
      options: {
        A: "10",
        B: "14",
        C: "16",
        D: String.raw`$2\sqrt{34}$`,
        E: String.raw`$10\sqrt{2}$`,
        F: String.raw`$8+2\sqrt{10}$`,
        G: String.raw`$16+2\sqrt{6}$`,
      },
      answer: "F",
      workedSolution: String.raw`The first circle has centre $(2,1)$ and radius $4$; the second has centre $(4,-5)$ and radius $4$. $P$ and $Q$ are furthest apart when they lie on the line through the two centres, on opposite outer sides.
The distance between the centres is
$$ \sqrt{(4-2)^2 + (-5-1)^2} = \sqrt{4 + 36} = 2\sqrt{10} $$
so the maximum length of $PQ$ is radius + centre distance + radius:
$$ 4 + 2\sqrt{10} + 4 = 8 + 2\sqrt{10} $$
which is option F.`,
    },
  ],
  ESAT: [
    {
      subject: "Mathematics",
      paperMeta: "Section 1",
      year: "ENGAA 2023",
      questionNumber: 7,
      text: String.raw`Given that
$$\frac{27^{2(x-2)}}{9^{(2x-3)}} = (81)^{\frac{3}{2}}$$
what is the value of $x$?`,
      options: {
        A: "0",
        B: "2.5",
        C: "3",
        D: "6",
        E: "7.5",
        F: "9",
        G: "10.5",
        H: "12",
      },
      answer: "D",
      workedSolution: String.raw`Express every base as a power of $3$: $27 = 3^3$, $9 = 3^2$, $81 = 3^4$.
$$ \frac{(3^3)^{2(x-2)}}{(3^2)^{2x-3}} = (3^4)^{\frac{3}{2}} $$
$$ \frac{3^{6x-12}}{3^{4x-6}} = 3^6 $$
Equating exponents:
$$ (6x-12)-(4x-6) = 6 $$
so $2x - 6 = 6$, giving $x = 6$, which is option D.`,
    },
    {
      subject: "Physics",
      paperMeta: "Section 1",
      year: "ENGAA 2023",
      questionNumber: 10,
      text: String.raw`Two samples of pure radioactive isotopes X and Y decay with half-lives of 2 days and 3 days, respectively.
Both X and Y decay in a single step into different stable isotopes.
Initially the number of atoms of X is twice the number of atoms of Y.
After how many days are the expected numbers of atoms of X and Y equal to each other?`,
      options: {
        A: "The expected numbers of atoms of X and Y are never equal.",
        B: "2 days",
        C: "3 days",
        D: "4 days",
        E: "6 days",
        F: "12 days",
      },
      answer: "E",
      workedSolution: String.raw`Let the initial numbers be $2N$ atoms of X and $N$ atoms of Y. After a time $t$,
$$ N_X = 2N\left(\tfrac{1}{2}\right)^{t/2} \qquad N_Y = N\left(\tfrac{1}{2}\right)^{t/3} $$
Setting them equal and writing both sides as powers of $2$:
$$ 2^{\,1-t/2} = 2^{-t/3} $$
Equating exponents:
$$ 1 - \frac{t}{2} = -\frac{t}{3} \implies 1 = \frac{t}{6} \implies t = 6 \text{ days} $$
which is option E. (Check: after 6 days X has halved three times, $2N \to \tfrac{N}{4}$; Y has halved twice, $N \to \tfrac{N}{4}$.)`,
    },
  ],
};
